import type { Brakesman } from './Filesystem/Helper';
import type { Lock, LockContext } from '@easterngraphics/wcf/modules/utils/async';

import { getContent } from '../utils/Download';
import { getUUID } from '../utils/Helper';

import { writeFile, ChunkSize } from './Filesystem/Helper';

import { getFileExtension } from '@egr/xbox/utils/String';

import { toBase64 } from '@easterngraphics/wcf/modules/utils/arraybuffer';
import { isNotNullOrEmpty, isNullOrEmpty } from '@easterngraphics/wcf/modules/utils/string';

interface ShareableEntryWithUri {
    uri: string | ArrayBuffer;
    contentType: string;
    filename?: string;
}

interface ShareableEntryWithPath {
    path: string;
    contentType: string;
    filename?: string;
    remove_path_after_share?: boolean;
}

export type ShareableEntry = ShareableEntryWithUri | ShareableEntryWithPath;

function getTempFilePath(value: ShareableEntry): string | null {
    let fileExtension: string;
    switch (value.contentType) {
        case 'image/png':
            fileExtension = 'png';
            break;
        case 'image/jpeg':
            fileExtension = 'jpeg';
            break;
        default:
            const extension = getFileExtension(value.filename);
            if (extension === '') {
                return null;
            }
            fileExtension = extension;
    }
    const filename: string = getUUID();
    return `/tmp/cache/${filename}.${fileExtension}`;
}

export interface SharePayloadEntry {
    action: 'fetch' | 'decode' | 'path';
    content: string;
    mimetype: string;
    filename: string;
    remove_path_after_share?: boolean;
}

function hasPath(value: ShareableEntry): value is ShareableEntryWithPath {
    return (value as ShareableEntryWithPath).path != null;
}

export async function getPayloadEntry(
    value: ShareableEntry,
    writeLock?: Lock,
    brakesman?: Brakesman
): Promise<SharePayloadEntry | null> {
    if (brakesman != null) {
        await brakesman();
    }

    const filename: string = isNotNullOrEmpty(value.filename) ? value.filename : getUUID();

    if (hasPath(value)) {
        if (isNullOrEmpty(value.path)) {
            return null;
        }

        return {
            action: 'path',
            content: value.path,
            mimetype: value.contentType,
            filename,
            remove_path_after_share: value.remove_path_after_share
        };
    } else if (
        value.uri instanceof ArrayBuffer ||
        value.uri.startsWith('blob:')
    ) {
        return await blobOrArrayBufferToPayloadEntry(value, writeLock);
    } else if (value.uri.startsWith('http')) {
        return {
            action: 'fetch',
            content: value.uri,
            mimetype: value.contentType,
            filename
        };
    } else if (/^\s*data:([a-z]+\/[a-z]+(;[a-z\-]+\=[a-z\-]+)?)?(;base64)?,[a-z0-9\!\$\&\'\,\(\)\*\+\,\;\=\-\.\_\~\:\@\/\?\%\s]*\s*$/i.test(value.uri)) {
        return {
            action: 'decode',
            content: value.uri.substring(value.uri.indexOf(',') + 1),
            mimetype: value.contentType,
            filename
        };
    } else if (/^\/tmp\/.*[^\/]$/.test(value.uri)) {
        return {
            action: 'path',
            content: value.uri,
            mimetype: value.contentType,
            filename
        };
    }
    return null;
}

async function blobOrArrayBufferToPayloadEntry(value: ShareableEntryWithUri, writeLock?: Lock): Promise<SharePayloadEntry | null> {
    const filePath: string | null = getTempFilePath(value);
    if (isNullOrEmpty(filePath)) {
        return null;
    }

    let buffer: ArrayBuffer;
    const filename: string = isNotNullOrEmpty(value.filename) ? value.filename : getUUID();
    if (value.uri instanceof ArrayBuffer) {
        buffer = value.uri;
    } else {
        buffer = await getContent(value.uri);
    }

    if (buffer.byteLength <= ChunkSize) {
        return {
            action: 'decode',
            content: toBase64(buffer),
            mimetype: value.contentType,
            filename
        };
    }

    let writeLockContext: LockContext | null = null;
    if (writeLock != null) {
        writeLockContext = await writeLock.acquireLock();
    }
    try {
        await writeFile(filePath, buffer);
        return {
            action: 'path',
            content: filePath,
            mimetype: value.contentType,
            filename
        };
    } catch {
        return null;
    } finally {
        if (writeLockContext != null) {
            writeLockContext.unlock();
        }
    }
}